import { ReactElement, Suspense, useMemo, useState } from 'react';
import {
  Route,
  RouteComponentProps,
  useLocation,
  withRouter
} from 'react-router-dom';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { mountPoint } from 'utils/generic/route-manipulation';
import { isMobile } from 'utils/is-mobile';
import {
  CasinoProvidersConfigsProvider,
  EProvidersLayout,
  ICasinoProvidersConfigs
} from './__partials/providers/CasinoProvidersConfigsContext';
import { CasinoProvidersSearchProvider } from './__partials/providers/CasinoProvidersSearchContext';
import { ProvidersDesktopSkeleton } from './__partials/ProvidersDesktop/Skeleton';
import { ProvidersMobileSkeleton } from './__partials/ProvidersMobile/Skeleton';
import { ProvidersTracker } from './__partials/ProvidersTracker';
import { ProvidersSkeleton } from './Skeleton';
import { ProvidersSliderSkeleton } from 'newelements/Casino/Providers/__partials/ProvidersSlider/skeleton';
import { ConditionalWrapper } from 'components/ConditionalWrapper';
import { useOptions } from '../hooks/useOptions';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

const { ProvidersDropdown } = lazy(
  () =>
    import(
      /* webpackChunkName: "providers-dropdown-view" */ './__partials/ProvidersDropdown'
    )
);

const { ProvidersSlider } = lazy(
  () =>
    import(
      /* webpackChunkName: "providers-slider-view" */ './__partials/ProvidersSlider'
    )
);

const { ProvidersMobile } = lazy(
  () =>
    import(
      /* webpackChunkName: "providers-tags-view-mobile" */ './__partials/ProvidersMobile'
    )
);

const { ProvidersDesktop } = lazy(
  () =>
    import(
      /* webpackChunkName: "providers-tags-view-desktop" */ './__partials/ProvidersDesktop'
    )
);

const LAYOUT = {
  dropdown: {
    view: ProvidersDropdown,
    skeleton: ProvidersSkeleton
  },
  slider: {
    view: ProvidersSlider,
    skeleton: ProvidersSliderSkeleton
  },
  desktop: {
    view: ProvidersDesktop,
    skeleton: ProvidersDesktopSkeleton
  },
  mobile: {
    view: ProvidersMobile,
    skeleton: ProvidersMobileSkeleton
  }
};

type ProvidersProps = RouteComponentProps & {
  configs: ICasinoProvidersConfigs;
};

const _Providers = ({ configs }: ProvidersProps): ReactElement => {
  useOptions({ shouldFetch: configs?.isFirstCasinoElement });
  const location = useLocation();

  const [searchValue, setSearchValue] = useState('');

  const layoutType = configs.providersLayout || EProvidersLayout.dropdown;

  const Layout = useMemo(() => {
    if (+layoutType === EProvidersLayout.dropdown) {
      return LAYOUT.dropdown;
    }

    if (+layoutType === EProvidersLayout.tags && configs.providersSliderMode) {
      return LAYOUT.slider;
    }

    return isMobile() ? LAYOUT.mobile : LAYOUT.desktop;
  }, [layoutType, configs.providersSliderMode]);

  return (
    <Route
      exact
      path={[
        '/',
        location.pathname,
        `${mountPoint(location.pathname, false, true)}/:category?`
      ]}
    >
      <div id="casino-providers-block">
        {/**  INDEPENDENT MOUNTING HANDLERS  |  START   **/}
        <ProvidersTracker />
        {/**  INDEPENDENT MOUNTING HANDLERS  |  END   **/}
        <ConditionalWrapper
          wrapper={children => (
            <div className="x-casinoProviders">{children}</div>
          )}
          condition={+layoutType === EProvidersLayout.dropdown}
        >
          <CasinoProvidersConfigsProvider value={{ configs }}>
            <CasinoProvidersSearchProvider
              value={{ searchValue, setSearchValue }}
            >
              <Suspense fallback={<Layout.skeleton />}>
                <Layout.view />
              </Suspense>
            </CasinoProvidersSearchProvider>
          </CasinoProvidersConfigsProvider>
        </ConditionalWrapper>
      </div>
    </Route>
  );
};

export const CasinoProviders: ReturnType<typeof withRouter> | any = withRouter(
  _Providers as any
);
